import { useEffect, useRef, useState } from "react";
import { Button } from "../components/rumonai";
import { clientErrorText, hub, type ClientAuthorization } from "./api";
import { ApiError } from "../lib/api";
import "./clients.css";

export function LocalDesktopConnect({ requestId, accountName, verifyAccount, onRefresh, onSwitchAccount }: {
  requestId: string; accountName: string; verifyAccount: () => Promise<boolean>; onRefresh: () => void; onSwitchAccount?: () => void;
}) {
  const [request, setRequest] = useState<ClientAuthorization | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const activeRequest = useRef(requestId);
  activeRequest.current = requestId;
  useEffect(() => {
    const controller = new AbortController();
    setRequest(null); setConfirmed(false); setApproved(false); setBusy(false); setError(null);
    hub.clientAuthorization(requestId, controller.signal).then(value => {
      if (!controller.signal.aborted) {
        setRequest(value);
        setApproved(value.status === "approved" || value.status === "complete");
      }
    }).catch(failure => { if (!controller.signal.aborted) setError(failure); });
    return () => controller.abort();
  }, [requestId, attempt]);
  const approve = async () => {
    if (busy || !confirmed || !request || request.id !== requestId || request.status !== "pending") return;
    const id = requestId;
    setBusy(true); setError(null);
    try {
      if (!(await verifyAccount()) || activeRequest.current !== id) return;
      await hub.approveClient(id);
      if (activeRequest.current !== id) return;
      setApproved(true); onRefresh();
    } catch (failure) { if (activeRequest.current === id) setError(failure); }
    finally { if (activeRequest.current === id) setBusy(false); }
  };
  const needsNewRequest = error instanceof ApiError && ["client_in_use", "client_import_changed", "client_plan_stale",
    "client_import_invalid", "client_credentials", "client_owner_mismatch", "client_authorization_expired"].includes(error.code || "");
  const wrongAccount = error instanceof ApiError && error.code === "client_owner_mismatch";
  return <section className="agent-creation local-desktop-connect">
    <header className="agent-create-head">
      <h1>{approved ? "已允许连接账号" : "连接这台电脑"}</h1>
      <p>{approved ? "客户端会自动连接。接下来在网页创建 Agent。" : "核对这台电脑和工作空间，再允许连接你的账号。"}</p>
    </header>
    {request ? <dl className="client-authorize-facts">
      <div><dt>电脑</dt><dd data-quoted="true">{request.device.name}</dd></div>
      <div><dt>系统</dt><dd data-quoted="true">{request.device.os} · {request.device.architecture}</dd></div>
      <div><dt>工作空间</dt><dd data-quoted="true">{request.workspace}</dd></div>
    </dl> : !error ? <p role="status">正在核对连接请求…</p> : null}
    {request?.imports?.length ? <section className="client-imports" aria-label="将继续使用的已有 Agent">
      <h2 className="settings-group-title">继续使用已有 Agent</h2>
      <ul>{request.imports.map(agent => <li key={agent.id}><span data-quoted="true">{agent.name}</span>
        {request.imports.filter(item => item.name === agent.name).length > 1 ? " · " + agent.id.slice(-6) : ""}
        {agent.keep_stopped ? " · 保持停止" : " · 保留原设置"}</li>)}</ul>
    </section> : null}
    {!approved && request ? <form className="agent-create-fields" onSubmit={event => { event.preventDefault(); void approve(); }}>
      <label className="client-consent"><input type="checkbox" checked={confirmed} disabled={busy || needsNewRequest}
        onChange={event => setConfirmed(event.target.checked)} />
        <span>确认这是我的电脑和工作空间，允许客户端连接账号“<span data-quoted="true">{accountName}</span>”。
          {request.imports?.length ? "继续使用上面的已有 Agent，不重新创建。" : ""}</span></label>
      <div className="agent-create-submit"><Button type="submit" disabled={!confirmed || busy || needsNewRequest || request.id !== requestId || request.status !== "pending"}>
        {busy ? "正在确认…" : "允许连接账号"}</Button></div>
    </form> : null}
    {approved && request ? <div className="settings-actions">
      <a className="btn btn-accent" href={"/#/connect?client_id=" + encodeURIComponent(request.client_id)}>新建 IP</a>
      <a className="btn btn-quiet" href={"/#/clients?client=" + encodeURIComponent(request.client_id)}>我的电脑</a>
    </div> : null}
    {error ? <div className="settings-actions" role="alert"><span>{clientErrorText(error)}</span>
      {wrongAccount && onSwitchAccount ? <Button variant="secondary" onClick={onSwitchAccount}>切换账号</Button>
        : needsNewRequest ? <a className="btn btn-quiet" href="/#/clients">重新连接</a> : null}
      {!needsNewRequest ? <Button variant="secondary" onClick={() => setAttempt(value => value + 1)}>重新核对</Button> : null}</div> : null}
  </section>;
}
